import {api} from '../../api.js';
import {AuthSnapshot} from '../../auth.js';
import {apiUrl} from '../../config.js';
import {escapeHtml, renderLayout} from '../../layout.js';
import {navigate} from '../../router.js';
import {getProfessionalModuleAccess,isBusinessFeatureEnabled,refreshProfessionalModuleAccess,type ProfessionalModuleAccess} from '../moduleAccess.js';
type TaskType = 'customs-invoice-matching'|'invoice-ledger'|'legal-review';
type ProfessionalModule = 'finance-management'|'legal-compliance-assistant';
type CloudTask = {id:string;name:string;companyName:string;organizationCode?:string;ledgerType?:TaskType;defaultCurrency?:string;refundMethod?:string;currentPlanId?:string;currentPlanVersion?:number;currentPlanSnapshot?:{name?:string;moduleVersions?:Record<string,string>}|null;status:string;createdAt:string;updatedAt:string};
type EnterpriseCandidate={name:string;creditCode:string;status:string;address:string};
const activeKey = (organizationId:string) => `tax-assistant-active-task:${organizationId}`;
const activeNameKey = (organizationId:string) => `tax-assistant-active-task-name:${organizationId}`;
const activeTypeKey = (organizationId:string) => `tax-assistant-active-task-type:${organizationId}`;
const legalActiveTaskKey = 'tax-assistant-active-legal-review-task';
const legalActiveTypeKey = 'tax-assistant-active-legal-review-task-type';
/** 任务行「编辑」图标（资料库/icon/编辑.svg）。 */
const TASK_ICON_EDIT='/assets/icons/edit.svg';
/** 任务行「删除」图标（资料库/icon/删除.svg）。 */
const TASK_ICON_DELETE='/assets/icons/delete.svg';

/** 记录跨空间可用的法务合同审核任务选择。 */
export function rememberLegalReviewTaskSelection(taskId: string, taskType: TaskType): void {
  if (taskType !== 'legal-review') return;
  localStorage.setItem(legalActiveTaskKey, taskId);
  localStorage.setItem(legalActiveTypeKey, taskType);
}

async function loadTasks():Promise<CloudTask[]> {return (await api.get<{tasks:CloudTask[]}>(apiUrl('/api/tasks'))).tasks;}

/** 法务任务数据写入个人空间，无需切换当前会话组织。 */
async function ensurePersonalWorkspaceForLegalTask(_auth: AuthSnapshot): Promise<boolean> {
  return true;
}
const normalizeTaskType=(value:unknown):TaskType => value==='invoice-ledger'||value==='legal-review'?value:'customs-invoice-matching';
const requestedLedgerType=():TaskType => normalizeTaskType(new URLSearchParams(window.location.search).get('ledgerType'));
const taskTypeLabel=(type?:TaskType) => type==='invoice-ledger'?'发票台账':type==='legal-review'?'合同文件审核':'报关单及发票匹配台账';
const professionalModule=(type?:TaskType):ProfessionalModule => normalizeTaskType(type)==='legal-review'?'legal-compliance-assistant':'finance-management';
const professionalModuleLabel=(type?:TaskType) => professionalModule(type)==='legal-compliance-assistant'?'法务合规助手模块':'财务管理模块';
/**
 * 财务管理模块当前仍开启的任务类型。
 */
function financeTaskTypes():TaskType[]{
  const types:TaskType[]=[];
  if(isBusinessFeatureEnabled('customs-invoice-matching-v1'))types.push('customs-invoice-matching');
  if(isBusinessFeatureEnabled('invoice-ledger-v1'))types.push('invoice-ledger');
  return types;
}
const taskTypeOptions=(module:ProfessionalModule,selected?:TaskType):string => {
  const options:TaskType[]=module==='legal-compliance-assistant'?['legal-review']:financeTaskTypes();
  return options.map(type=>`<option value="${type}"${type===selected?' selected':''}>${escapeHtml(taskTypeLabel(type))}</option>`).join('');
};

/** 当前账号可创建任务的专业模块（双模块均有授权时默认财务）。 */
function preferredCreatableModule(access:ProfessionalModuleAccess):ProfessionalModule|null{
  if(access['finance-management']&&financeTaskTypes().length)return 'finance-management';
  if(access['legal-compliance-assistant'])return 'legal-compliance-assistant';
  if(access['finance-management'])return 'finance-management';
  return null;
}

/** 专业模块对应的默认可选任务类型。 */
function defaultTaskTypeForModule(module:ProfessionalModule):TaskType{
  if(module==='legal-compliance-assistant')return 'legal-review';
  return financeTaskTypes()[0]??'customs-invoice-matching';
}
const taskEntryRoute=(task:Pick<CloudTask,'ledgerType'>) => {const type=normalizeTaskType(task.ledgerType);return type==='invoice-ledger'?'/tax-assistant/invoices':type==='legal-review'?'/tax-assistant/legal-review':'/tax-assistant/declarations';};

function enterpriseFieldMarkup(value:string,creditCode:string,enabled:boolean):string{
  if(!enabled)return `<input class="form-input" name="companyName" maxlength="200" value="${escapeHtml(value)}" placeholder="请输入本任务所属企业名称" required>`;
  return `<div class="enterprise-picker" data-enterprise-picker>
    <input class="form-input" name="companyName" maxlength="200" value="${escapeHtml(value)}" placeholder="输入企业名称关键字，选择工商登记企业" autocomplete="off" required>
    <input type="hidden" name="enterpriseCreditCode" value="${escapeHtml(creditCode)}">
    <div class="enterprise-suggestions" data-enterprise-suggestions hidden></div>
    <div class="enterprise-selection-hint" data-enterprise-hint>${creditCode?'已选择工商登记企业':'请输入至少 2 个字进行工商企业检索'}</div>
  </div>`;
}

function bindEnterprisePicker(root:HTMLElement):void{
  const picker=root.querySelector<HTMLElement>('[data-enterprise-picker]');
  if(!picker)return;
  const input=picker.querySelector<HTMLInputElement>('input[name="companyName"]');
  const code=picker.querySelector<HTMLInputElement>('input[name="enterpriseCreditCode"]');
  const list=picker.querySelector<HTMLElement>('[data-enterprise-suggestions]');
  const hint=picker.querySelector<HTMLElement>('[data-enterprise-hint]');
  if(!input||!code||!list)return;
  let timer:number|undefined;
  let requestId=0;
  const hide=()=>{list.hidden=true;list.innerHTML='';};
  const renderResults=(results:EnterpriseCandidate[])=>{
    if(!results.length){list.innerHTML='<div class="enterprise-suggestion-empty">未查询到符合条件的企业</div>';list.hidden=false;return;}
    list.innerHTML=results.map((item,index)=>`<button type="button" class="enterprise-suggestion" data-enterprise-index="${index}"><strong>${escapeHtml(item.name)}</strong><span>${escapeHtml(item.creditCode)} · ${escapeHtml(item.status)}</span></button>`).join('');
    list.hidden=false;
    list.querySelectorAll<HTMLButtonElement>('[data-enterprise-index]').forEach(button=>button.addEventListener('click',()=>{
      const item=results[Number(button.dataset.enterpriseIndex)];
      if(!item)return;
      input.value=item.name;code.value=item.creditCode;
      const visibleCode=picker.closest('form')?.querySelector<HTMLInputElement>('input[name="organizationCode"]');
      if(visibleCode)visibleCode.value=item.creditCode;
      if(hint)hint.textContent=`已选择：${item.name}`;
      hide();
    }));
  };
  input.addEventListener('input',()=>{
    code.value='';
    if(hint)hint.textContent='请从下拉结果中选择企业';
    if(timer)window.clearTimeout(timer);
    const keyword=input.value.trim();
    if(keyword.length<2){hide();return;}
    timer=window.setTimeout(async()=>{
      const current=++requestId;
      list.innerHTML='<div class="enterprise-suggestion-loading">正在查询…</div>';list.hidden=false;
      try{
        const result=await api.get<{configured:boolean;results:EnterpriseCandidate[];unavailable?:boolean}>(apiUrl(`/api/tasks/enterprise/search?keyword=${encodeURIComponent(keyword)}`),{timeoutMs:10000});
        if(current!==requestId)return;
        if(result.unavailable){list.innerHTML='<div class="enterprise-suggestion-empty">工商查询暂不可用，可直接输入企业名称</div>';list.hidden=false;return;}
        if(!result.configured){list.innerHTML='<div class="enterprise-suggestion-empty">工商数据接口未配置</div>';list.hidden=false;return;}
        renderResults(result.results||[]);
      }catch(error){if(current!==requestId)return;list.innerHTML=`<div class="enterprise-suggestion-empty">查询失败</div>`;list.hidden=false;}
    },350);
  });
  input.addEventListener('focus',()=>{if(input.value.trim().length>=2&&!code.value)input.dispatchEvent(new Event('input'));});
  document.addEventListener('click',event=>{if(!picker.contains(event.target as Node))hide();},{once:false});
}

function formatDate(iso:string):string{
  const d=new Date(iso);
  const now=new Date();
  const diff=now.getTime()-d.getTime();
  const day=86400000;
  if(diff<day)return d.toLocaleTimeString('zh-CN',{hour:'2-digit',minute:'2-digit'});
  if(diff<day*2)return '昨天';
  if(diff<day*7)return `${Math.floor(diff/day)}天前`;
  return d.toLocaleDateString('zh-CN',{year:'numeric',month:'2-digit',day:'2-digit'});
}

export async function renderTaskPage(root:HTMLElement,auth:AuthSnapshot):Promise<void>{
  renderLayout(root,auth,'<div class="tasks-page"><div class="tp-loading">加载中…</div></div>');
  try{
    await refreshProfessionalModuleAccess();
    const tasks=await loadTasks();
    const activeId=localStorage.getItem(activeKey(auth.organization.id))??'';
    const activeTask=tasks.find(task=>task.id===activeId);
    if(activeTask){
      localStorage.setItem(activeNameKey(auth.organization.id),activeTask.name);
      localStorage.setItem(activeTypeKey(auth.organization.id),normalizeTaskType(activeTask.ledgerType));
      rememberLegalReviewTaskSelection(activeTask.id, normalizeTaskType(activeTask.ledgerType));
    }else if(activeId){
      localStorage.removeItem(activeKey(auth.organization.id));
      localStorage.removeItem(activeNameKey(auth.organization.id));
      localStorage.removeItem(activeTypeKey(auth.organization.id));
    }
    const hasTypeFilter=new URLSearchParams(window.location.search).has('ledgerType');
    const visibleTasks=hasTypeFilter?tasks.filter(task=>(task.ledgerType||'customs-invoice-matching')===requestedLedgerType()):tasks;
    renderTaskList(root,auth,visibleTasks,activeTask?.id??'');
  }catch(error){renderLayout(root,auth,`<div class="tasks-page"><div class="tp-error">${escapeHtml(error instanceof Error?error.message:String(error))}</div></div>`);}
}

function renderTaskList(root:HTMLElement,auth:AuthSnapshot,tasks:CloudTask[],activeId:string):void{
  renderLayout(root,auth,`<div class="tasks-page">
    <header class="tp-page-head">
      <div class="tp-page-head-main">
        <h1 class="tp-title">任务</h1>
      </div>
    </header>

    <div class="tp-toolbar">
      <div class="tp-filter-bar" role="tablist" aria-label="任务筛选">
        <button type="button" class="tp-chip is-active" data-task-filter="all" role="tab" aria-selected="true">全部</button>
        <button type="button" class="tp-chip" data-task-filter="finance-management" role="tab" aria-selected="false">财务管理</button>
        <button type="button" class="tp-chip" data-task-filter="legal-compliance-assistant" role="tab" aria-selected="false">合同文件审核</button>
      </div>
      <div class="tp-toolbar-right">
        <div class="tp-search-group">
          <div class="tp-search">
            <svg class="tp-search-icon" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><circle cx="6" cy="6" r="4.5" stroke="currentColor" stroke-width="1.4"/><path d="m9.5 9.5 3 3" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
            <input class="tp-search-input" id="taskListSearch" placeholder="搜索任务名称或类型" aria-label="搜索任务">
          </div>
          <button class="tp-btn-search" id="taskSearchButton" type="button">搜索</button>
        </div>
        <button class="tp-btn-primary" id="taskNewButton" type="button">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M7 1v12M1 7h12" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
          新建任务
        </button>
      </div>
    </div>

    ${tasks.length?tasks.map(task=>taskRow(task,activeId)).join(''):`
    <div class="tp-empty">
      <div class="tp-empty-icon" aria-hidden="true">
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none"><rect x="9" y="6" width="22" height="28" rx="3.5" stroke="currentColor" stroke-width="1.6"/><path d="M15 17h10M15 22h10M15 27h7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
      </div>
      <h3>还没有任务</h3>
      <p>创建第一个任务，开始处理报关、发票或合规审核工作</p>
      <button class="tp-btn-primary" id="taskEmptyCreateButton" type="button">新建任务</button>
    </div>`}
  </div>`);

  const openCreate=async()=>{await refreshProfessionalModuleAccess();renderTaskCreate(root,auth,tasks,activeId);};
  root.querySelector('#taskNewButton')?.addEventListener('click',openCreate);
  root.querySelector('#taskEmptyCreateButton')?.addEventListener('click',openCreate);
  let activeFilter='all';
  const applyFilters=()=>{
    const input=root.querySelector<HTMLInputElement>('#taskListSearch');
    const q=(input?.value||'').trim().toLowerCase();
    root.querySelectorAll<HTMLElement>('[data-task-search]').forEach(row=>{
      const matchesSearch=!q||String(row.dataset.taskSearch||'').toLowerCase().includes(q);
      const matchesFilter=activeFilter==='all'||row.dataset.taskModule===activeFilter;
      row.hidden=!(matchesSearch&&matchesFilter);
    });
  };
  root.querySelectorAll<HTMLButtonElement>('[data-task-filter]').forEach(button=>button.addEventListener('click',()=>{
    activeFilter=button.dataset.taskFilter||'all';
    root.querySelectorAll<HTMLButtonElement>('[data-task-filter]').forEach(chip=>{
      const active=chip===button;
      chip.classList.toggle('is-active',active);
      chip.setAttribute('aria-selected',active?'true':'false');
    });
    applyFilters();
  }));
  root.querySelector<HTMLInputElement>('#taskListSearch')?.addEventListener('input',applyFilters);
  root.querySelector('#taskSearchButton')?.addEventListener('click',applyFilters);
  root.querySelector<HTMLInputElement>('#taskListSearch')?.addEventListener('keydown',event=>{
    if(event.key==='Enter'){event.preventDefault();applyFilters();}
  });
  root.querySelectorAll<HTMLButtonElement>('[data-activate]').forEach(btn=>btn.addEventListener('click',async()=>{
    const tt=normalizeTaskType(btn.dataset.taskType);
    const module=professionalModule(tt);
    await refreshProfessionalModuleAccess();
    if(!getProfessionalModuleAccess()[module]){
      window.alert(`您尚未取得${professionalModuleLabel(tt)}使用授权。请联系系统管理员为当前账户所属空间开通后再继续使用该任务。`);
      return;
    }
    localStorage.setItem(activeKey(auth.organization.id),btn.dataset.activate??'');
    localStorage.setItem(activeNameKey(auth.organization.id),btn.dataset.taskName??'');
    localStorage.setItem(activeTypeKey(auth.organization.id),tt);
    rememberLegalReviewTaskSelection(btn.dataset.activate ?? '', tt);
    navigate(btn.dataset.taskRoute||taskEntryRoute({ledgerType:tt}));
  }));
  root.querySelectorAll<HTMLButtonElement>('[data-edit-task]').forEach(btn=>btn.addEventListener('click',()=>{
    const task=tasks.find(t=>t.id===btn.dataset.editTask);
    if(task)renderTaskEdit(root,auth,task,tasks,activeId);
  }));
  root.querySelectorAll<HTMLButtonElement>('[data-delete]').forEach(btn=>btn.addEventListener('click',async()=>{
    const tid=btn.dataset.delete??'';
    if(!tid||!window.confirm('确定删除该任务吗？'))return;
    try{await api.delete(apiUrl(`/api/tasks/${encodeURIComponent(tid)}`));
      if(localStorage.getItem(activeKey(auth.organization.id))===tid){
        localStorage.removeItem(activeKey(auth.organization.id));
        localStorage.removeItem(activeNameKey(auth.organization.id));
        localStorage.removeItem(activeTypeKey(auth.organization.id));
      }
      await renderTaskPage(root,auth);
    }catch(e){window.alert(e instanceof Error?e.message:String(e));}
  }));
}

function taskRow(task:CloudTask,activeId:string):string{
  const active=task.id===activeId;
  const moduleKey=professionalModule(task.ledgerType);
  const search=escapeHtml(`${task.name} ${task.companyName||''} ${professionalModuleLabel(task.ledgerType)} ${taskTypeLabel(task.ledgerType)}`);
  return `<article class="tp-row${active?' is-active':''}" role="listitem" data-task-search="${search}" data-task-module="${moduleKey}">
    <div class="tp-row-main">
      <div class="tp-row-top">
        <h2 class="tp-row-name">${escapeHtml(task.name)}</h2>
      </div>
      <div class="tp-row-meta">
        <span class="tp-meta-chip tp-meta-chip-module">${escapeHtml(professionalModuleLabel(task.ledgerType))}</span>
        <span class="tp-meta-chip tp-meta-chip-type">${escapeHtml(taskTypeLabel(task.ledgerType))}</span>
        <span class="tp-row-time">${formatDate(task.updatedAt)}</span>
      </div>
    </div>
    <div class="tp-row-actions">
      <button class="tp-btn-icon" type="button" data-edit-task="${escapeHtml(task.id)}" title="编辑任务" aria-label="编辑任务">
        <img src="${TASK_ICON_EDIT}" alt="" aria-hidden="true">
      </button>
      <button class="tp-btn-icon tp-btn-icon-danger" type="button" data-delete="${escapeHtml(task.id)}" title="删除任务" aria-label="删除任务">
        <img src="${TASK_ICON_DELETE}" alt="" aria-hidden="true">
      </button>
      <button class="tp-btn-go${active?' is-primary':''}" type="button" data-activate="${escapeHtml(task.id)}" data-task-name="${escapeHtml(task.name)}" data-task-type="${escapeHtml(task.ledgerType||'customs-invoice-matching')}" data-task-route="${taskEntryRoute(task)}">
        ${active?'继续任务':'进入'}
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="m4.5 2.5 3.5 3.5-3.5 3.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>
    </div>
  </article>`;
}

function renderTaskEdit(root:HTMLElement,auth:AuthSnapshot,task:CloudTask,tasks:CloudTask[],activeId:string):void{
  const back=()=>void renderTaskPage(root,auth);
  renderLayout(root,auth,`<div class="tasks-page">
    <div class="tp-form">
      <div class="tp-form-head">
        <button class="tp-btn-text" id="feBack"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M9 2 4 7l5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>返回</button>
        <h2>编辑任务</h2>
      </div>
      <form id="feForm" class="tp-form-body">
        <label class="tp-field"><span>任务名称</span><input class="tp-input" name="name" maxlength="120" value="${escapeHtml(task.name)}" required></label>
        <label class="tp-field"><span>企业名称</span>${enterpriseFieldMarkup(task.companyName,task.organizationCode||'',true)}</label>
        <label class="tp-field"><span>统一社会信用代码</span><input class="tp-input" name="organizationCode" maxlength="40" value="${escapeHtml(task.organizationCode||'')}" placeholder="选择企业后自动填充" readonly></label>
        <div class="tp-form-msg" id="feMsg"></div>
        <div class="tp-form-actions">
          <button type="button" class="tp-btn-text" id="feCancel">取消</button>
          <button type="submit" class="tp-btn-primary">保存</button>
        </div>
      </form>
    </div>
  </div>`);
  bindEnterprisePicker(root);
  root.querySelector('#feBack')?.addEventListener('click',back);
  root.querySelector('#feCancel')?.addEventListener('click',back);
  root.querySelector<HTMLFormElement>('#feForm')?.addEventListener('submit',async e=>{
    e.preventDefault();
    const form=e.currentTarget as HTMLFormElement;
    const d=new FormData(form);
    const msg=root.querySelector<HTMLElement>('#feMsg');
    try{
      if(msg)msg.textContent='';
      const r=await api.patch<{task:CloudTask}>(apiUrl(`/api/tasks/${encodeURIComponent(task.id)}`),{
        name:String(d.get('name')??''),companyName:String(d.get('companyName')??''),
        enterpriseCreditCode:String(d.get('enterpriseCreditCode')??d.get('organizationCode')??''),
        organizationCode:String(d.get('enterpriseCreditCode')??d.get('organizationCode')??''),
      });
      if(localStorage.getItem(activeKey(auth.organization.id))===task.id)localStorage.setItem(activeNameKey(auth.organization.id),r.task.name);
      await renderTaskPage(root,auth);
    }catch(err){if(msg)msg.textContent=err instanceof Error?err.message:String(err);}
  });
}

function renderTaskCreate(root:HTMLElement,auth:AuthSnapshot,tasks:CloudTask[],activeId:string):void{
  const back=()=>void renderTaskPage(root,auth);
  const fixed=new URLSearchParams(window.location.search).has('ledgerType');
  let ledgerType=requestedLedgerType();
  const moduleAccess=getProfessionalModuleAccess();
  if(fixed){
    const lockedModule=professionalModule(ledgerType);
    if(!moduleAccess[lockedModule]){renderLayout(root,auth,`<div class="legacy-loading">您尚未取得${escapeHtml(professionalModuleLabel(ledgerType))}使用授权。请联系系统管理员为当前账户所属空间开通后再创建对应任务。</div>`);return;}
    if(lockedModule==='finance-management'&&!financeTaskTypes().includes(ledgerType)){renderLayout(root,auth,`<div class="legacy-loading">${escapeHtml(taskTypeLabel(ledgerType))}已关闭。请联系系统管理员启用后再创建任务。</div>`);return;}
  }else{
    const preferred=preferredCreatableModule(moduleAccess);
    if(!preferred){renderLayout(root,auth,'<div class="legacy-loading">您尚未取得专业模块使用授权。请联系系统管理员为当前账户开通后再创建任务。</div>');return;}
    if(!moduleAccess[professionalModule(ledgerType)])ledgerType=defaultTaskTypeForModule(preferred);
  }
  if(!fixed&&professionalModule(ledgerType)==='finance-management'){
    if(!financeTaskTypes().length){renderLayout(root,auth,'<div class="legacy-loading">财务管理子功能均已关闭。请联系系统管理员启用后再创建任务。</div>');return;}
    if(!financeTaskTypes().includes(ledgerType))ledgerType=defaultTaskTypeForModule('finance-management');
  }
  const initialModule=professionalModule(ledgerType);
  const moduleControl=fixed
    ?`<div class="tp-input tp-input-readonly">${escapeHtml(professionalModuleLabel(ledgerType))}<input type="hidden" name="professionalModule" value="${initialModule}"></div>`
    :`<select class="tp-select" name="professionalModule">${moduleAccess['finance-management']&&financeTaskTypes().length?'<option value="finance-management">财务管理模块</option>':''}${moduleAccess['legal-compliance-assistant']?'<option value="legal-compliance-assistant">法务合规助手模块</option>':''}</select>`;
  const typeControl=fixed
    ?`<div class="tp-input tp-input-readonly">${escapeHtml(taskTypeLabel(ledgerType))}<input type="hidden" name="ledgerType" value="${ledgerType}"></div>`
    :`<select class="tp-select" name="ledgerType">${taskTypeOptions(initialModule,ledgerType)}</select>`;
  renderLayout(root,auth,`<div class="tasks-page">
    <div class="tp-form">
      <div class="tp-form-head">
        <button class="tp-btn-text" id="fcBack"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M9 2 4 7l5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>返回</button>
        <h2>新建任务</h2>
      </div>
      <form id="fcForm" class="tp-form-body">
        <label class="tp-field"><span>专业模块</span>${moduleControl}</label>
        <label class="tp-field"><span>任务类型</span>${typeControl}</label>
        <label class="tp-field"><span>任务名称</span><input class="tp-input" name="name" maxlength="120" placeholder="请输入任务名称" required></label>
        <div class="tp-form-msg" id="fcMsg"></div>
        <div class="tp-form-actions">
          <button type="button" class="tp-btn-text" id="fcCancel">取消</button>
          <button type="submit" class="tp-btn-primary">创建</button>
        </div>
      </form>
    </div>
  </div>`);
  if(!fixed){
    const moduleSelect=root.querySelector<HTMLSelectElement>('select[name="professionalModule"]');
    const typeSelect=root.querySelector<HTMLSelectElement>('select[name="ledgerType"]');
    moduleSelect?.addEventListener('change',()=>{
      const module=moduleSelect.value==='legal-compliance-assistant'?'legal-compliance-assistant':'finance-management';
      if(typeSelect)typeSelect.innerHTML=taskTypeOptions(module);
    });
  }
  root.querySelector('#fcBack')?.addEventListener('click',back);
  root.querySelector('#fcCancel')?.addEventListener('click',back);
  root.querySelector<HTMLFormElement>('#fcForm')?.addEventListener('submit',async e=>{
    e.preventDefault();
    const form=e.currentTarget as HTMLFormElement;
    const d=new FormData(form);
    const msg=root.querySelector<HTMLElement>('#fcMsg');
    try{
      if(msg)msg.textContent='';
      const rt=normalizeTaskType(d.get('ledgerType'));
      if(rt==='legal-review'&&!(await ensurePersonalWorkspaceForLegalTask(auth)))return;
      const r=await api.post<{task:CloudTask}>(apiUrl('/api/tasks'),{
        name:String(d.get('name')??''),
        ledgerType:rt,
      });
      const tt=normalizeTaskType(r.task.ledgerType||rt);
      localStorage.setItem(activeKey(auth.organization.id),r.task.id);
      localStorage.setItem(activeNameKey(auth.organization.id),r.task.name);
      localStorage.setItem(activeTypeKey(auth.organization.id),tt);
      rememberLegalReviewTaskSelection(r.task.id, tt);
      navigate(taskEntryRoute({ledgerType:tt}));
    }catch(err){if(msg)msg.textContent=err instanceof Error?err.message:String(err);}
  });
}

export function activeTaskId(auth:AuthSnapshot):string{return localStorage.getItem(activeKey(auth.organization.id))??'';}

/** 读取法务合同审核任务 ID（跨个人/团队空间会话）。 */
export function activeLegalReviewTaskId(): string {
  return localStorage.getItem(legalActiveTaskKey) ?? '';
}

/** 读取法务合同审核任务类型标记。 */
export function activeLegalReviewTaskType(): TaskType {
  return normalizeTaskType(localStorage.getItem(legalActiveTypeKey));
}
